<template>

  <form @submit.prevent="handleSubmit">

    <div class="form-group">
      <label class="form-label required"><i class="fas fa-image mr-1"></i>Event Year</label>
      <select name="eventYear" id="eventYear" class="form-control col-md-3" v-model="model.eventYear"
        v-on:change="getCategories(model.eventYear)" required>
        <option value="0" selected disabled>Select an event year</option>
        <option v-for="data in eventYearOptions" :key="data" :value="data.value">
          {{ data.label }}
        </option>
      </select>
    </div>

    <div class="form-group">
      <label class="form-label required"><i class="fas fa-image"></i>Category</label>
      <select name="eventDetailId" id="eventDetailId" class="form-control col-md-3" v-model="model.eventDetailId" required>
        <option value="0" selected disabled>Select an event</option>
        <option v-for="data in categories" :key="data" :value="data.value">
          {{ data.label }}
        </option>
      </select>
    </div>

    <!-- File Upload Section -->
    <div class="form-group">
      <label class="form-label required"><i class="fas fa-upload mr-1"></i>Upload Images</label>

      <!-- Upload Grid -->
      <div class="upload-grid">
        <!-- Uploaded Files Cards -->
        <div v-for="(file, index) in selectedFiles" :key="index" class="upload-card uploaded-card">
          <div class="image-preview">
            <img :src="file.preview" :alt="file.name" />
            <div class="image-overlay">
              <button type="button" class="btn btn-danger btn-sm" @click.stop="removeFile(index)" title="Remove Image">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>

          <!-- File Metadata Form -->
          <div class="file-metadata">
            <!-- Title -->
            <div class="form-group">
              <label class="form-label">Title</label>
              <InputText v-model="file.title" placeholder="Enter image title" class="form-control form-control-sm"
                :class="{
                  'is-invalid': getValidationError(
                    `files.${index}.title`
                  ),
                }" />
              <div v-if="getValidationError(`files.${index}.title`)" class="invalid-feedback">
                {{ getValidationError(`files.${index}.title`) }}
              </div>
            </div>

            <!-- Description -->
            <div class="form-group">
              <label class="form-label">Description</label>
              <Textarea v-model="file.description" placeholder="Enter image description" rows="2"
                class="form-control form-control-sm" :class="{
                  'is-invalid': getValidationError(
                    `files.${index}.description`
                  ),
                }" />
              <div v-if="getValidationError(`files.${index}.description`)" class="invalid-feedback">
                {{ getValidationError(`files.${index}.description`) }}
              </div>
            </div>

            <!-- Date -->
            <div class="form-group">
              <label class="form-label">Date</label>
              <Calendar v-model="file.date" placeholder="Select date" class="form-control form-control-sm" :class="{
                'is-invalid': getValidationError(`files.${index}.date`),
              }" dateFormat="yy-mm-dd" :showIcon="true" iconDisplay="input" />
              <div v-if="getValidationError(`files.${index}.date`)" class="invalid-feedback">
                {{ getValidationError(`files.${index}.date`) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Empty Upload Card -->
        <div class="upload-card empty-card" :class="{ 'drag-over': isDragOver }" @drop="handleDrop"
          @dragover.prevent="handleDragOver" @dragleave="handleDragLeave" @click="triggerFileInput">
          <div class="empty-card-content">
            <i class="fas fa-plus fa-3x text-muted mb-3"></i>
            <h6 class="text-muted mb-2">Add Photo</h6>
            <p class="text-muted small mb-0">Click or drag to upload</p>
            <small class="text-muted">PNG, JPEG, JPG</small>
          </div>
        </div>
      </div>

      <!-- Hidden File Input -->
      <input ref="fileInput" type="file" multiple accept="image/png,image/jpeg,image/jpg" @change="handleFileSelect"
        style="display: none" />

      <!-- File Upload Error -->
      <div v-if="uploadError" class="alert alert-danger mt-2">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        {{ uploadError }}
      </div>
    </div>

    <!-- Upload Progress -->
    <div v-if="isUploading" class="upload-progress mb-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span>Uploading {{ uploadProgress.current }} of
          {{ uploadProgress.total }} files...</span>
        <span>{{ Math.round(uploadProgress.percentage) }}%</span>
      </div>
      <div class="progress">
        <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
          :style="{ width: uploadProgress.percentage + '%' }" :aria-valuenow="uploadProgress.percentage"
          aria-valuemin="0" aria-valuemax="100"></div>
      </div>
    </div>

    <!-- Form Actions -->
    <div class="form-actions">
      <div class="d-flex justify-content-between">
        <button type="button" class="btn btn-secondary" @click="handleCancel" :disabled="isUploading">
          <i class="fas fa-times"></i>
          Cancel
        </button>

        <div>
          <button type="button" class="btn btn-outline-primary mr-2" @click="resetForm" :disabled="isUploading">
            <i class="fas fa-undo"></i>
            Reset
          </button>

          <button type="submit" class="btn btn-primary" :disabled="!isFormValid || isUploading || selectedFiles.length === 0
            ">
            <i class="fas fa-save"></i>
            {{
              isUploading
                ? "Uploading..."
                : isEditMode
                  ? "Update Gallery"
                  : "Upload Photos"
            }}
          </button>
        </div>
      </div>
    </div>
  </form>


  <div v-if="uploadSuccess" class="alert alert-success mt-3">
    <i class="fas fa-check-circle mr-2"></i>
    {{ uploadSuccessMessage }}
  </div>


</template>

<script>
import { defineComponent } from "vue";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Calendar from "primevue/calendar";
import apiService from "@/services/ApiService";

export default defineComponent({
  name: "PhotoGalleryForm",
  components: {
    InputText,
    Textarea,
    Calendar,
  },
  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    existingData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      model: {
        eventYear: "",
        eventDetailId: "",
        photos: [],
      },
      categories: [{ label: "Launch", value: "launch" }, { label: "Dinner", value: "dinner" }],
      eventYearOptions: [{ label: "2023", value: "2023" }, { label: "2024", value: "2024" }, { label: "2025", value: "2025" }],
      selectedFiles: [],
      isDragOver: false,
      isUploading: false,
      uploadError: null,
      uploadSuccess: false,
      uploadSuccessMessage: "",
      validationErrors: {},
      uploadProgress: {
        current: 0,
        total: 0,
        percentage: 0,
      },
      allowedFormats: ["image/png", "image/jpeg", "image/jpg"],
      maxFileSize: 10 * 1024 * 1024, // 10MB
    };
  },
  computed: {
    isFormValid() {
      if (this.selectedFiles.length === 0) return false;
      if (!this.model.eventYear || this.model.eventYear === "0") return false;
      if (!this.model.eventDetailId || this.model.eventDetailId === "0") return false;

      return this.selectedFiles.every((file) => {
        return (
          file.title &&
          file.title.trim() !== "" &&
          file.description &&
          file.description.trim() !== "" &&
          file.date
        );
      });
    },
  },
  methods: {
    getCategories(year) {
      apiService.get(`/PhotoGallery/categories/${year}`)
        .then(response => {
          this.categories = response.data;
        }).catch(error => {
          console.error(error);
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: error.response.data.Message,
            life: 5000
          });
        });
    },
    // File handling methods
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
      // Reset file input
      event.target.value = "";
    },

    handleDrop(event) {
      event.preventDefault();
      this.isDragOver = false;

      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },

    handleDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },

    handleDragLeave() {
      this.isDragOver = false;
    },
    processFiles(files) {
      this.uploadError = null;
      const validFiles = [];

      files.forEach((file) => {
        // Validate file type
        if (!this.allowedFormats.includes(file.type)) {
          this.uploadError = `Invalid file format: ${file.name}. Only PNG, JPEG, and JPG files are allowed.`;
          return;
        }

        // Validate file size
        if (file.size > this.maxFileSize) {
          this.uploadError = `File too large: ${file.name}. Maximum size is 10MB.`;
          return;
        }

        // Create file object with metadata
        const fileObj = {
          file: file,
          name: file.name,
          size: file.size,
          preview: URL.createObjectURL(file),
          title: "", // Let user fill this manually
          description: "",
          date: null, // Let user select date manually
        };

        validFiles.push(fileObj);
      });

      if (validFiles.length > 0) {
        this.selectedFiles.push(...validFiles);
      }
    },

    removeFile(index) {
      // Revoke object URL to prevent memory leak
      URL.revokeObjectURL(this.selectedFiles[index].preview);
      this.selectedFiles.splice(index, 1);

      // Clear validation errors for this file
      Object.keys(this.validationErrors).forEach((key) => {
        if (key.startsWith(`files.${index}.`)) {
          delete this.validationErrors[key];
        }
      });
    },

    // Validation methods
    validateForm() {
      this.validationErrors = {};

      this.selectedFiles.forEach((file, index) => {
        if (!file.title || file.title.trim() === "") {
          this.validationErrors[`files.${index}.title`] = "Title is required";
        }

        if (!file.description || file.description.trim() === "") {
          this.validationErrors[`files.${index}.description`] =
            "Description is required";
        }

        if (!file.date) {
          this.validationErrors[`files.${index}.date`] = "Date is required";
        }
      });

      return Object.keys(this.validationErrors).length === 0;
    },

    getValidationError(field) {
      return this.validationErrors[field];
    },

    // Form submission
    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.isUploading = true;
      this.uploadProgress.total = this.selectedFiles.length;
      this.uploadProgress.current = 0;
      this.uploadProgress.percentage = 0;

      try {
        // Create FormData for all files
        const formData = new FormData();
        
        // Add event detail ID
        formData.append('EventDetailId', this.model.eventDetailId);
        
        // Add all files and their metadata
        this.selectedFiles.forEach((file) => {
          formData.append('Files', file.file);
          formData.append('Titles', file.title);
          formData.append('Descriptions', file.description);
          formData.append('Dates', file.date ? file.date.toISOString().split('T')[0] : '');
        });

        // Upload all files at once
        const response = await apiService.post('/PhotoGallery/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress.percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            this.uploadProgress.current = this.selectedFiles.length;
          }
        });

        // Upload completed successfully
        this.uploadSuccess = true;
        this.uploadSuccessMessage = response.data.message || `Successfully uploaded ${this.selectedFiles.length} photo(s) to the gallery!`;

        // Emit success event
        this.$emit("upload-success", {
          files: this.selectedFiles,
          message: this.uploadSuccessMessage,
          response: response.data
        });

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          this.uploadSuccess = false;
        }, 5000);

        // Reset form after successful upload
        this.resetForm();
      } catch (error) {
        console.error('Upload error:', error);
        this.uploadError = error.response?.data?.message || error.message || "An error occurred during upload";
        this.$emit("upload-error", error);
      } finally {
        this.isUploading = false;
      }
    },

    async uploadFile(fileData) {
      // This method is no longer used but kept for backward compatibility
      // The actual upload is now handled in handleSubmit
      return { success: true, fileData };
    },

    // Form actions
    handleCancel() {
      if (this.isUploading) {
        if (
          confirm("Upload is in progress. Are you sure you want to cancel?")
        ) {
          this.isUploading = false;
        } else {
          return;
        }
      }

      this.$emit("cancel");
      this.resetForm();
    },

    resetForm() {
      // Revoke all object URLs
      this.selectedFiles.forEach((file) => {
        URL.revokeObjectURL(file.preview);
      });

      this.model.eventYear = "";
      this.model.eventDetailId = "";
      this.selectedFiles = [];
      this.isDragOver = false;
      this.isUploading = false;
      this.uploadError = null;
      this.uploadSuccess = false;
      this.uploadSuccessMessage = "";
      this.validationErrors = {};
      this.uploadProgress = {
        current: 0,
        total: 0,
        percentage: 0,
      };
    },
  },

  beforeUnmount() {
    // Clean up object URLs
    this.selectedFiles.forEach((file) => {
      URL.revokeObjectURL(file.preview);
    });
  },
});
</script>

<style scoped>
.photo-gallery-form {
  max-width: 100%;
}

.required::after {
  content: " *";
  color: #dc3545;
}

/* Upload Grid */
.upload-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.upload-card {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  overflow: hidden;
  background: white;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 280px;
}

.upload-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Uploaded Card */
.uploaded-card .image-preview {
  position: relative;
  height: 200px;
  overflow: hidden;
  background-color: #f8f9fa;
}

.uploaded-card .image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.upload-card:hover .image-overlay {
  opacity: 1;
}

.file-metadata {
  padding: 1rem;
  height: calc(100% - 200px);
  overflow-y: auto;
}

.file-metadata .form-group {
  margin-bottom: 1rem;
}

.file-metadata .form-group:last-child {
  margin-bottom: 0;
}

/* Empty Card */
.empty-card {
  border: 2px dashed #dee2e6;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-card:hover {
  border-color: #4e73df;
  background-color: #f0f3ff;
}

.empty-card.drag-over {
  border-color: #4e73df;
  background-color: #e3f2fd;
  transform: scale(1.02);
}

.empty-card-content {
  text-align: center;
  padding: 2rem;
}

.empty-card-content h6 {
  font-weight: 600;
  color: #495057;
}

.empty-card-content p {
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.empty-card-content small {
  font-size: 0.75rem;
}

.form-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #495057;
  font-size: 0.875rem;
}

.form-check-container {
  display: flex;
  gap: 1rem;
}

.form-check-label {
  font-size: 0.875rem;
  color: #6c757d;
}

/* Upload Progress */
.upload-progress {
  background: #f8f9fa;
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #dee2e6;
}

/* Form Actions */
.form-actions {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

/* Responsive Design */
@media (max-width: 768px) {
  .upload-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .upload-card {
    min-height: 240px;
  }

  .uploaded-card .image-preview {
    height: 150px;
  }

  .file-metadata {
    padding: 0.75rem;
    height: calc(100% - 150px);
  }

  .empty-card-content {
    padding: 1.5rem;
  }

  .form-actions .d-flex {
    flex-direction: column;
    gap: 1rem;
  }

  .form-actions .d-flex>div {
    display: flex;
    gap: 0.5rem;
  }

  .form-check-container {
    flex-direction: column;
    gap: 0.5rem;
  }
}

@media (max-width: 576px) {
  .upload-grid {
    gap: 0.75rem;
  }

  .upload-card {
    min-height: 220px;
  }

  .uploaded-card .image-preview {
    height: 120px;
  }

  .file-metadata {
    padding: 0.5rem;
    height: calc(100% - 120px);
  }

  .empty-card-content {
    padding: 1rem;
  }

  .empty-card-content h6 {
    font-size: 0.9rem;
  }

  .empty-card-content p {
    font-size: 0.8rem;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .upload-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (min-width: 1025px) {
  .upload-grid {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }
}

/* Animation */
.upload-card {
  animation: fadeInUp 0.3s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* PrimeVue Components Styling */
:deep(.p-inputtext) {
  border-radius: 0.375rem;
  border-color: #ced4da;
}

:deep(.p-inputtext:focus) {
  border-color: #4e73df;
  box-shadow: 0 0 0 0.2rem rgba(78, 115, 223, 0.25);
}

:deep(.p-calendar) {
  width: 100%;
}

:deep(.p-calendar .p-inputtext) {
  width: 100%;
  padding-right: 2.5rem;
}

:deep(.p-calendar .p-datepicker-trigger) {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  padding: 0;
  width: auto;
  height: auto;
}

:deep(.p-calendar .p-datepicker-trigger:hover) {
  color: #4e73df;
}

:deep(.p-calendar .p-datepicker-trigger .p-icon) {
  width: 1rem;
  height: 1rem;
}

:deep(.p-calendar.p-inputwrapper) {
  position: relative;
  display: flex;
}

/* Error States */
.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}
</style>
